// The "your message is waiting / queued" banner (agent_not_run) ends once the AGENT wrote something
// AFTER the prompt it is about. The prompt is the anchor, never a row count: a history page or a
// ledger merge landing after the banner puts OLDER rows into the transcript, and they answer nothing.
import { isTaskNotificationItem } from "./task-notification-prompt.js";

interface Row {
  kind: string;
  name?: string;
  id?: string;
  ts?: number;
  content?: string;
  clientMessageId?: string;
  messageId?: string;
}
export interface NotRunAnchor {
  id?: string;
  clientMessageId?: string;
  messageId?: string;
  content?: string;
  raisedAt: number;
  // The newest row when the prompt was (re)sent: a retry resends in place, above older rows.
  tailId?: string;
}

// Same rule as the live stream (isTaskNotificationRow): a task notification answers nothing.
const isAnswer = (e: Row): boolean =>
  e.kind !== "user_message" &&
  e.kind !== "turn_boundary" &&
  !isTaskNotificationItem({ type: e.kind, name: e.name, text: e.content });

export function notRunAnchor(entries: readonly Row[], raisedAt: number): NotRunAnchor {
  const prompt = entries.findLast((e) => e.kind === "user_message");
  return {
    id: prompt?.id,
    clientMessageId: prompt?.clientMessageId,
    messageId: prompt?.messageId,
    content: prompt?.content,
    raisedAt,
  };
}

// A durable row can replace the optimistic prompt under a new id: ids first, the same words last.
function anchorIndex(entries: readonly Row[], anchor: NotRunAnchor): number {
  const same = (pick: (e: Row) => string | undefined, want: string | undefined): number =>
    want === undefined
      ? -1
      : entries.findLastIndex((e) => e.kind === "user_message" && pick(e) === want);
  for (const at of [
    same((e) => e.id, anchor.id),
    same((e) => e.clientMessageId, anchor.clientMessageId),
    same((e) => e.messageId, anchor.messageId),
    same((e) => e.content, anchor.content),
  ]) {
    if (at >= 0) return at;
  }
  return -1;
}

export function answeredSince(entries: readonly Row[], anchor: NotRunAnchor): boolean {
  const hadPrompt = anchor.id !== undefined;
  if (!hadPrompt) {
    return entries.some((e) => isAnswer(e) && e.ts !== undefined && e.ts > anchor.raisedAt);
  }
  const at = anchorIndex(entries, anchor);
  // The prompt is off screen while a read swaps the rows: nothing can be said yet.
  if (at < 0) return false;
  const tail = anchor.tailId === undefined ? -1 : entries.findIndex((e) => e.id === anchor.tailId);
  return entries.slice(Math.max(at, tail) + 1).some(isAnswer);
}
